import { Injectable } from "@angular/core";
import { SearchDisplayMode, SearchInputType } from "../../../model";

/**
 * Types of actions that can be blocked
 */
export type BlockedActionType = "execution" | "autocomplete" | "suggestion" | "place_lookup";

/**
 * Result of validation check
 */
export interface ValidationResult {
  valid: boolean;
  reasons?: string[];
  blockedType?: BlockedActionType;
}

/**
 * Unified input validator using the new SearchInputType enum
 * Replaces the old SearchInputValidator with cleaner logic
 */
@Injectable({
  providedIn: "root",
})
export class SearchInputValidator {
  /**
   * Validates if input type is allowed for execution in current display mode
   * @param inputType The detected input type
   * @param displayMode Current search display mode
   * @returns ValidationResult indicating if execution is allowed
   */
  validateInputForExecution(inputType: SearchInputType, displayMode: SearchDisplayMode): ValidationResult {
    // AI and Property modes: block all execution
    if (displayMode === SearchDisplayMode.AI || displayMode === SearchDisplayMode.PROPERTY) {
      return {
        valid: false,
        reasons: [`Search is not available in ${displayMode.toUpperCase()} mode`],
        blockedType: "execution",
      };
    }

    // Map mode: allow all input types
    if (displayMode === SearchDisplayMode.MAP) {
      return { valid: true };
    }

    // Grid mode: only allow address/plain text input
    if (displayMode === SearchDisplayMode.GRID) {
      if (inputType === SearchInputType.COORDINATE) {
        return {
          valid: false,
          reasons: ["Coordinate search is not available in Grid mode. Switch to Map mode to search by coordinates."],
          blockedType: "execution",
        };
      }

      if (inputType === SearchInputType.WKT_GEOMETRY) {
        return {
          valid: false,
          reasons: ["Geometry search is not available in Grid mode. Switch to Map mode to search by geometry."],
          blockedType: "execution",
        };
      }

      // Allow Google Places, plain text, and history
      return { valid: true };
    }

    // Default fallback - should not reach here
    return { valid: false, reasons: ["Unknown display mode"], blockedType: "execution" };
  }

  /**
   * Validates if input type should be shown in autocomplete for current display mode
   * @param inputType The type of input being evaluated
   * @param displayMode Current search display mode
   * @returns ValidationResult indicating if suggestion should be shown
   */
  validateSuggestionForAutocomplete(inputType: SearchInputType, displayMode: SearchDisplayMode): ValidationResult {
    // AI and Property modes: block all autocomplete
    if (displayMode === SearchDisplayMode.AI || displayMode === SearchDisplayMode.PROPERTY) {
      return {
        valid: false,
        reasons: ["Autocomplete is not available in this mode"],
        blockedType: "autocomplete",
      };
    }

    // Map mode: allow all suggestion types
    if (displayMode === SearchDisplayMode.MAP) {
      return { valid: true };
    }

    // Grid mode: only allow plain text, places, and history suggestions
    if (displayMode === SearchDisplayMode.GRID) {
      if (inputType === SearchInputType.COORDINATE || inputType === SearchInputType.WKT_GEOMETRY) {
        return {
          valid: false,
          reasons: ["Coordinate and geometry suggestions are not shown in Grid mode"],
          blockedType: "suggestion",
        };
      }

      // Allow Google Places, plain text, and history
      return { valid: true };
    }

    return { valid: false, reasons: ["Unknown display mode"], blockedType: "suggestion" };
  }

  /**
   * Validates if Google Places API should be called for current display mode
   * @param displayMode Current display mode
   * @returns ValidationResult with Places API permission
   */
  validatePlacesApiCall(displayMode: SearchDisplayMode): ValidationResult {
    // AI and Property modes: no Places API
    if (displayMode === SearchDisplayMode.AI || displayMode === SearchDisplayMode.PROPERTY) {
      return {
        valid: false,
        reasons: ["Places API is not available in this mode"],
        blockedType: "place_lookup",
      };
    }

    // Map and Grid modes: allow Places API
    if (displayMode === SearchDisplayMode.MAP || displayMode === SearchDisplayMode.GRID) {
      return { valid: true };
    }

    return { valid: false, reasons: ["Unknown display mode"], blockedType: "place_lookup" };
  }

  /**
   * Gets human-readable reason why an action was blocked
   * @param inputType The input type that was blocked
   * @param displayMode Current display mode
   * @param actionType Type of action that was blocked
   * @returns User-friendly explanation
   */
  getBlockedReason(inputType: SearchInputType, displayMode: SearchDisplayMode, actionType: BlockedActionType): string {
    switch (actionType) {
      case "execution":
        if (displayMode === SearchDisplayMode.GRID) {
          if (inputType === SearchInputType.COORDINATE) {
            return "Coordinate search requires Map mode. Switch to Map view to search by coordinates.";
          }
          if (inputType === SearchInputType.WKT_GEOMETRY) {
            return "Geometry search requires Map mode. Switch to Map view to search by geometry.";
          }
        }
        return `Search is not available in ${displayMode.toUpperCase()} mode.`;

      case "suggestion":
        return "This type of suggestion is not available in the current view mode.";

      case "place_lookup":
        return "Place suggestions are not available in the current mode.";

      default:
        return "This action is not available in the current mode.";
    }
  }

  /**
   * Get allowed input types for a display mode
   * @param displayMode Current display mode
   * @returns Array of allowed input types
   */
  getAllowedInputTypes(displayMode: SearchDisplayMode): SearchInputType[] {
    if (displayMode === SearchDisplayMode.AI || displayMode === SearchDisplayMode.PROPERTY) {
      return [];
    }

    if (displayMode === SearchDisplayMode.MAP) {
      return [
        SearchInputType.COORDINATE,
        SearchInputType.WKT_GEOMETRY,
        SearchInputType.GOOGLE_PLACE,
        SearchInputType.TEXT,
      ];
    }

    if (displayMode === SearchDisplayMode.GRID) {
      return [SearchInputType.GOOGLE_PLACE, SearchInputType.TEXT];
    }

    return [];
  }
}
